import { Box, Button, Center, ChakraProvider } from '@chakra-ui/react';
import { ReactComponent as VenngageLogoIcon } from 'assets/icons/venngage_logo.svg';
import { STANDALONE_API_KEY } from 'constants/infograph';
import { GeneratorPaths, Routes } from 'constants/routes';
import {
  fetchE2APITokenByInfographId,
  fetchE2APITokenByPrivateLinkId,
  fetchE2APITokenByPublicLinkId,
} from 'libs/e1api/design';
import { ReactElement, Suspense, lazy } from 'react';
import {
  LoaderFunction,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useNavigate,
} from 'react-router-dom';
import { isStandalone } from 'utils/environment';

import './app.css';
// We can move this to editor container if other view no need translation
import './i18n';
import { theme } from './theme';

const Editor = lazy(
  () =>
    import(
      /* webpackChunkName: "Editor" */
      /* webpackPrefetch: true */
      './modules/Editor'
    ),
);

const Export = lazy(
  () =>
    import(
      /* webpackChunkName: "Export" */
      './modules/Export'
    ),
);

const PrivateLinkView = lazy(
  () =>
    import(
      /* webpackChunkName: "PrivateLinkView" */
      /* webpackPrefetch: true */
      './modules/PrivateLinkView'
    ),
);

const PublicLinkView = lazy(
  () =>
    import(
      /* webpackChunkName: "PublicLinkView" */
      './modules/PublicLinkView'
    ),
);

const CardGenerator = lazy(
  () =>
    import(
      /* webpackChunkName: "CardGenerator" */
      './modules/DesignGenerator/CardGenerator'
    ),
);

const DesignGenerator = lazy(
  () =>
    import(
      /* webpackChunkName: "DesignAI" */
      './modules/DesignGenerator/DesignAI'
    ),
);

const DesignCreate = lazy(
  () =>
    import(
      /* webpackChunkName: "DesignCreate" */
      './modules/ComponentPage/CreateDesign'
    ),
);

const ComponentPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ComponentPage" */
      './modules/ComponentPage'
    ),
);

const Admin = lazy(
  () =>
    import(
      /* webpackChunkName: "Admin" */
      './modules/Admin'
    ),
);

const BrandTemplatePreview = lazy(
  () =>
    import(
      /* webpackChunkName: "BrandTemplatePreview" */
      './modules/Onboarding/BrandTemplatePreview'
    ),
);

const HRTeams = lazy(
  () =>
    import(
      /* webpackChunkName: "HRTeams" */
      './modules/HRTeams'
    ),
);

const ResponsiveLinkView = lazy(
  () =>
    import(
      /* webpackChunkName: "ResponsiveLinkView" */
      './modules/ResponsiveLinkView'
    ),
);

const SuspenseEditor = (): ReactElement => (
  // TODO: Need a fancy UI spinner
  <Suspense fallback={<LoadingScreen />}>
    <Editor />
  </Suspense>
);

const SuspenseExport = (): ReactElement => (
  // Empty component fallback for export view
  <Suspense fallback={<></>}>
    <Export />
  </Suspense>
);

const SuspensePrivateLinkView = (): ReactElement => (
  // Empty component fallback for export view
  <Suspense fallback={<></>}>
    <PrivateLinkView />
  </Suspense>
);

const SuspensePublicLinkView = (): ReactElement => (
  // Empty component fallback for export view
  <Suspense fallback={<></>}>
    <PublicLinkView />
  </Suspense>
);

const SuspenseAdmin = (): ReactElement => (
  <Suspense fallback={'loading ...'}>
    <Admin />
  </Suspense>
);

const SuspenseCardGenerator = (): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <CardGenerator />
  </Suspense>
);

const SuspenseComponentPage = (): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <ComponentPage />
  </Suspense>
);

const SuspenseDesignGenerator = (): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <DesignGenerator />
  </Suspense>
);

const SuspenseDesignCreate = (): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <DesignCreate />
  </Suspense>
);

const SuspenseOnboarding = (): ReactElement => (
  <Suspense fallback={<></>}>
    <BrandTemplatePreview />
  </Suspense>
);

const SuspenseHRTeamsDashboard = (): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <HRTeams />
  </Suspense>
);

const SuspenseResponsiveLinkView = (): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <ResponsiveLinkView />
  </Suspense>
);

/**
 * Double-clicking on the logo navigates to admin page.
 * @constructor
 */
const JustLogo = (): ReactElement => {
  let navigate = useNavigate();

  function handleClick() {
    navigate('/admin');
  }

  return (
    <Center h='100vh'>
      <Box alignItems='center' display='flex' flexDirection='column'>
        <VenngageLogoIcon color='var(--vg-colors-brand-500)' />
        <Button size='sm' variant='action' mt={3} onClick={handleClick}>
          Admin
        </Button>
      </Box>
    </Center>
  );
};

const LoadingScreen = () => <Box>loading...</Box>;

/**
 * Fetch JWT token using e2 or e1 infograph id from API
 * If it is standalone, return the standalone API key
 * @param {LoaderParams}
 * @returns
 */
const tokenLoader: LoaderFunction = async ({ params }) => {
  const { id, privateLinkId, publicLinkId } = params;

  if (isStandalone) return STANDALONE_API_KEY;

  if (privateLinkId) {
    return (await fetchE2APITokenByPrivateLinkId(privateLinkId))?.token;
  } else if (publicLinkId) {
    return (await fetchE2APITokenByPublicLinkId(publicLinkId))?.token;
  } else if (id) {
    return (await fetchE2APITokenByInfographId(id))?.token;
  }
};

// Set the tokenLoader to use E2ClientProvider.
const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path='design/create' element={<SuspenseDesignCreate />} loader={tokenLoader} />
      <Route path='design/:id' element={<SuspenseEditor />} loader={tokenLoader} />
      <Route path='pl/:privateLinkId' element={<SuspensePrivateLinkView />} loader={tokenLoader} />
      <Route path='pe/:publicLinkId' element={<SuspensePublicLinkView />} loader={tokenLoader} />
      <Route path='rl/:privateLinkId' element={<SuspenseResponsiveLinkView />} loader={tokenLoader} />
      <Route path='export/:id' element={<SuspenseExport />} loader={tokenLoader} />
      <Route path='export/:id/:pageId' element={<SuspenseExport />} loader={tokenLoader} />
      <Route path='onboarding/*' element={<SuspenseOnboarding />} />
      <Route path={GeneratorPaths.Card} element={<SuspenseCardGenerator />} />
      <Route path={GeneratorPaths.Infographic} element={<SuspenseDesignGenerator />} />
      <Route path={GeneratorPaths.Chart} element={<SuspenseDesignGenerator />} />
      <Route path={GeneratorPaths.Poster} element={<SuspenseDesignGenerator />} />
      <Route path={Routes.HR_TEAMS} element={<SuspenseHRTeamsDashboard />} />
      <Route path='ai-tools/*' element={<SuspenseComponentPage />} />
      <Route path='features/*' element={<SuspenseComponentPage />} />
      <Route path='admin' element={<SuspenseAdmin />} />
      <Route path='*' element={<JustLogo />} />
    </>,
  ),
);

export const App = (): ReactElement => (
  <ChakraProvider theme={theme}>
    <RouterProvider router={router} />
  </ChakraProvider>
);

export default App;
